import * as React from "react";
import { createRoot } from "react-dom/client";
import { api, type ApiOrg, type ApiUser } from "./api.js";
import { LoginScreen } from "./screens/Login.js";
import { Dashboard } from "./screens/Dashboard.js";
import { AttendeeOverridesScreen } from "./screens/AttendeeOverrides.js";
import { WebhooksScreen } from "./screens/Webhooks.js";

type Route = "dashboard" | "overrides" | "webhooks";

function routeFromHash(): Route {
  if (window.location.hash === "#/overrides") return "overrides";
  if (window.location.hash === "#/webhooks") return "webhooks";
  return "dashboard";
}

function App() {
  const [state, setState] = React.useState<{ user: ApiUser | null; org: ApiOrg | null; instance: string | null; loading: boolean }>({
    user: null, org: null, instance: null, loading: true,
  });
  const [route, setRoute] = React.useState<Route>(routeFromHash);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await api.me();
        let org: ApiOrg | null = null;
        try { org = await api.org(); } catch { /* leave null */ }
        if (!alive) return;
        setState({ user: me.user, org, instance: me.instance, loading: false });
      } catch {
        if (!alive) return;
        setState({ user: null, org: null, instance: null, loading: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    const handler = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  if (state.loading) return <div className="login-shell"><div className="muted">Loading…</div></div>;
  if (!state.user) return <LoginScreen instance={state.instance} />;
  if (route === "overrides") {
    return <AttendeeOverridesScreen user={state.user} org={state.org} instance={state.instance ?? "production"} />;
  }
  if (route === "webhooks") {
    return <WebhooksScreen user={state.user} org={state.org} instance={state.instance ?? "production"} />;
  }
  return <Dashboard user={state.user} org={state.org} instance={state.instance ?? "production"} />;
}

const root = document.getElementById("root");
if (root) createRoot(root).render(<App />);
